import {createStyles} from '@mantine/core';
import React, {ReactNode} from 'react';

/**
 * Wrapper component for ContextPanel content that applies consistent styling.
 *
 * This allows children of the FlowchartContextPanel to denote what is a header and what is content.
 * For instance, in the below example the Tab container renders its own header, so we only want
 * the the tab contents to be wrapped in this component.
 *
 * ┌─────────────────────────────────┐
 * │ FlowchartContextPanel           │
 * │┌───────────────────────────────┐│
 * ││ TabContainer (no padding)     ││ <- Full-bleed tab header
 * ││ [Tab 1] [Tab 2]               ││
 * │├───────────────────────────────┤│
 * ││ ┌───────────────────────────┐ ││ <- Padded tab content
 * ││ │ ContextPanelContent       │ ││
 * ││ │   (padding: 16px)         │ ││
 * ││ │   {tab content}           │ ││
 * ││ └───────────────────────────┘ ││
 * │└───────────────────────────────┘│
 * └─────────────────────────────────┘
 *
 * @example
 * // For tabbed content - wrap each tab's children
 * <TabContainer ...>
 *   <Tab ...>
 *     <ContextPanelContent>
 *       {tabContent}
 *     </ContextPanelContent>
 *   </Tab>
 * </TabContainer>

* @example
 * // For content without tabs
 * <FlowchartContextPanel ...>
 *   <ContextPanelContent>
 *     {content}
 *   </ContextPanelContent>
 * </FlowchartContextPanel>
 *

 */
const useStyles = createStyles((theme) => ({
  paddedContent: {
    padding: theme.spacing.lg,
    height: '100%',
  },
}));

export function ContextPanelContent({children}: {children: ReactNode}): JSX.Element {
  const {classes} = useStyles();
  return <div className={classes.paddedContent}>{children}</div>;
}
